import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { btnLinkCls, btnPrimaryCls, btnSecondaryCls } from "components/tw/classes";
import StatusChip, { ChipTone } from "components/tw/status-chip";
import { fetchReviewQueue, ReviewQueueAnswer, ReviewQueueItem, SignStatus } from "services/cabinet-queues.service";
import { vilniusYmd } from "utils/vilnius-date";
import { companyOptions } from "./company-options";
import { FilterSelect, SearchBox } from "./filter-controls";
import { withThousands } from "./format-count";
import { LoadFailedNotice, QueueSkeleton } from "./queue-list-states";
import { QueueCell, QueueRow, QueueTable } from "./queue-table";
import QueueSheet from "./queue-sheet";
import { useQueryParams } from "./use-query-params";
import { useQueueData } from "./use-queue-data";
import { useQueueWalk } from "./use-queue-walk";

const PAGE_SIZE = 100;

// How a document's signing reads in the list: its words and colour.
const SIGN_STATUS_CHIPS: Record<SignStatus, { labelKey: string; tone: ChipTone }> = {
  signed: { labelKey: "signed", tone: "green" },
  pending: { labelKey: "pending", tone: "amber" },
  not_initiate: { labelKey: "not_initiate", tone: "grey" },
};
// On a phone the page buttons get words next to the arrows and a 44 px height.
const PAGE_BUTTON_PHONE_CLS = "max-md:min-h-11 max-md:px-4";
const SEARCH_DELAY_MS = 300;
const MIN_SEARCH_LENGTH = 2;

/** «Peržiūrėti dokumentus»: the documents sent to the person for review, paged, and the sheet that goes through them one by one. */
export default function ReviewQueuePage() {
  const { t } = useTranslation();
  const { companyId = "" } = useParams();
  const { params, change } = useQueryParams();

  const page = Math.max(1, Math.floor(Number(params.get("page"))) || 1);
  const company = params.get("company") ?? "";
  const search = params.get("q") ?? "";
  // Shorter than two characters the search is not sent.
  const searchText = search.trim().length >= MIN_SEARCH_LENGTH ? search.trim() : undefined;
  const filters = { company: company || undefined, q: searchText, pageSize: PAGE_SIZE };

  // Reviewing the last rows of the last page leaves that page empty: step back to the last page that has rows.
  const stepBackFromEmptyPage = (answer: ReviewQueueAnswer) => {
    if (answer.items.length > 0 || page <= 1) return;
    const lastPage = Math.max(1, Math.ceil(answer.total / PAGE_SIZE));
    change({ page: lastPage > 1 ? String(lastPage) : "" });
  };

  const list = useQueueData(() => fetchReviewQueue(companyId, { ...filters, page }), {
    scopeKey: companyId,
    requestKey: JSON.stringify([filters, page]),
    onAnswer: stepBackFromEmptyPage,
  });
  const walk = useQueueWalk(companyId, list.reload);

  const answer = list.data;
  const items = answer?.items ?? [];
  const total = answer?.total ?? 0;
  const firstOnPage = (page - 1) * PAGE_SIZE;
  const lastOnPage = firstOnPage + items.length;

  // The columns of the list, left to right; the cells of each row follow the same order.
  const columns = [
    { heading: t("queue_col_title"), width: "minmax(0,4fr)" },
    { heading: t("queue_col_company"), width: "minmax(0,2fr)" },
    { heading: t("sign_status"), width: "9rem" },
    { heading: t("queue_col_received"), width: "6rem" },
    { heading: "", width: "7rem" },
  ];

  // The walk over the queue starts at the first row on this page and goes on through the rest of the queue.
  const openWholeQueue = () =>
    walk.open({
      entries: items.map((item) => ({ kind: "review", item })),
      total,
      offset: firstOnPage,
      loadRowsAfter: (cursor) => fetchReviewQueue(companyId, { ...filters, after: cursor }).then((next) => next.items),
    });

  const openOneDocument = (item: ReviewQueueItem) =>
    walk.open({ entries: [{ kind: "review", item }], total: 1, offset: 0 });

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-base font-semibold text-gray-900">
            {answer ? t("queue_review_status", { count: total, formatted: withThousands(total) }) : ""}
          </p>
          <p className="mt-1 text-sm text-gray-500">{t("queue_review_not_counted")}</p>
        </div>
        <button
          type="button"
          className={`${btnPrimaryCls} w-full md:w-auto`}
          disabled={items.length === 0 || list.isLoading}
          onClick={openWholeQueue}
        >
          {t("queue_review_start")}
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <FilterSelect
          label={t("queue_filter_company")}
          value={company}
          options={companyOptions(answer?.companies ?? [], company, t("queue_filter_all_companies"))}
          onChange={(value) => change({ company: value, page: "" })}
        />
        <SearchBox
          label={t("queue_search")}
          value={search}
          delayMs={SEARCH_DELAY_MS}
          onSearch={(text) => change({ q: text, page: "" })}
        />
      </div>

      {list.hasFailed && <LoadFailedNotice onRetry={list.reload} />}
      {!list.data && list.isLoading && <QueueSkeleton />}

      {items.length > 0 && (
        <div className={list.isLoading ? "opacity-60" : ""}>
          <QueueTable label={t("tasks_queue_review")} columns={columns} tableFrom="xl">
            {items.map((item) => (
              <QueueRow key={item.id} onOpen={() => openOneDocument(item)}>
                <QueueCell isTitle>
                  <Link to={item.link} className={btnLinkCls}>
                    {item.title}
                  </Link>
                </QueueCell>
                <QueueCell label={t("queue_col_company")}>{item.companyName}</QueueCell>
                <QueueCell>
                  <StatusChip tone={SIGN_STATUS_CHIPS[item.signStatus].tone}>
                    {t(SIGN_STATUS_CHIPS[item.signStatus].labelKey)}
                  </StatusChip>
                </QueueCell>
                <QueueCell label={t("queue_col_received")}>{vilniusYmd(item.since)}</QueueCell>
                <QueueCell className="ml-auto xl:ml-0 xl:text-right">
                  <button type="button" className={btnSecondaryCls} onClick={() => openOneDocument(item)}>
                    {t("tasks_action_view")}
                  </button>
                </QueueCell>
              </QueueRow>
            ))}
          </QueueTable>

          <div className="mt-3 flex items-center justify-end gap-2 text-sm text-gray-600">
            <span>
              {t("queue_page_range", {
                from: withThousands(firstOnPage + 1),
                to: withThousands(lastOnPage),
                total: withThousands(total),
              })}
            </span>
            <button
              type="button"
              className={`${btnSecondaryCls} ${PAGE_BUTTON_PHONE_CLS}`}
              aria-label={t("queue_prev_page")}
              disabled={page <= 1}
              onClick={() => change({ page: page > 2 ? String(page - 1) : "" })}
            >
              ‹<span className="md:hidden"> {t("back")}</span>
            </button>
            <button
              type="button"
              className={`${btnSecondaryCls} ${PAGE_BUTTON_PHONE_CLS}`}
              aria-label={t("queue_next_page")}
              disabled={lastOnPage >= total}
              onClick={() => change({ page: String(page + 1) })}
            >
              <span className="md:hidden">{t("next")} </span>›
            </button>
          </div>
        </div>
      )}

      <QueueSheet walk={walk} />
    </div>
  );
}
